import { ADDITIONAL_SPACE_ON_SIDES, STATION_SIZE } from 'constants/map';
import { Path } from 'pages/Map/MyMap/Map/components/ZoneMap/components/Path';
import { BigJunctureStation, SubZoneStation } from 'pages/Map/MyMap/Map/components/ZoneMap/components/Station';
import { PADDING_AROUND_SUBZONE_CHILDREN } from 'pages/Map/MyMap/Map/components/ZoneMap/components/Station/components/SubZone/SubZoneStation';
import { buildStation } from 'pages/Map/MyMap/Map/components/ZoneMap/components/Station/utils';
import {
    APIPathData,
    APISectionData,
    APIStationData,
    ExpandedStation,
    SectionData,
    Station,
    StationData,
    StationType,
    Zone
} from 'pages/Map/types';
import { PathNodeCollection } from './types';

/**
 * This method prepares flat list of stations(including stations inside subzones)
 */
export const getStationsDataFromZone = (zone: Zone, parentStation?: Station): Station[] => {
    const stations = zone.stations ?? [];
    return stations.reduce((zoneStations: Station[], station: APIStationData) => {
        const { type, zone } = station;
        const id = station.id ?? station.name;

        if (type === StationType.SubZone && zone) {
            const currentStation = new SubZoneStation({
                ...station,
                id,
                type: StationType.SubZone,
                parentStation,
                zone
            });

            const subZoneStations = getStationsDataFromZone(currentStation.zone, currentStation);

            return [...zoneStations, currentStation, ...subZoneStations];
        }

        const currentStation = buildStation({
            ...station,
            id,
            parentStation
        } as StationData);

        return [...zoneStations, currentStation];
    }, []);
};

/**
 * While generating map data, Map producer considers Zone start station xPosition as 0, stations left to it as -values and right to it as +values.
 * But in our map canvas position starts with 0, so we are calculating lowest value in entire zone and add this to all station positions while rendering to place them properly in view
 */
export const getLowestXValueInMap = (stations: Station[]) => {
    let lowestStation;
    for (const station of stations) {
        lowestStation = lowestStation ?? station;
        if (station.xPosition < lowestStation.xPosition) {
            lowestStation = station;
        }
    }
    return lowestStation?.getXValue() ?? 0;
};

/**
 * Our map renders with absolute positions, finding max xy value of map.
 * This helps us to know width and height of the map
 */
export const getHighestAbsolutePosition = (stations: Station[], expandedStation?: ExpandedStation) => {
    return stations.reduce(
        (currentMax, station) => {
            if (!station.isHidden(expandedStation)) {
                const { x, y } = station.getAbsolutePosition();
                const PaddingForSubzoneStation = PADDING_AROUND_SUBZONE_CHILDREN / 2;
                return {
                    x: Math.max(
                        currentMax.x,
                        x + STATION_SIZE + ADDITIONAL_SPACE_ON_SIDES.right + (station.parentStation ? PaddingForSubzoneStation : 0)
                    ),
                    y: Math.max(currentMax.y, y + STATION_SIZE + ADDITIONAL_SPACE_ON_SIDES.bottom)
                };
            }
            return currentMax;
        },
        { x: 0, y: 0 }
    );
};

export const getStation = (stations: Station[], id: string) => {
    for (const station of stations) {
        if (station instanceof BigJunctureStation) {
            const matchingStation = station.getStations().find(station => station.getId() === id);
            if (matchingStation) {
                return matchingStation;
            }
        }
        if (station.getId() === id) {
            return station;
        }
    }

    return;
};

const mapAPIPathDataToPath = (path: APIPathData, stations: Station[]) => {
    const { start, end, ...rest } = path;

    const sourceId = start.id ?? start.name;

    const targetId = end.id ?? end.name;

    const source = stations.find(station => station.getId() === sourceId);
    const target = stations.find(station => station.getId() === targetId);

    if (!source || !target) {
        return;
    }

    return new Path({
        source,
        target,
        ...rest
    });
};

/**
 * This method prepares flat list of paths(including paths inside subzones)
 */
export const getPathsDataFromZone = (zone: Zone, allStations: Station[]) => {
    if (!zone.paths) {
        return [];
    }
    const paths = zone.paths.map(path => mapAPIPathDataToPath(path, allStations)).filter(path => path instanceof Path) as Path[];

    const stations = getStationsDataFromZone(zone);
    const subPaths = stations.reduce((zonePaths: Path[], station: Station): Path[] => {
        if (station instanceof SubZoneStation) {
            const subPaths = getPathsDataFromZone(station.zone, allStations).filter(path => path instanceof Path) as Path[];
            return [...zonePaths, ...subPaths];
        }

        return zonePaths;
    }, []);

    return [...paths, ...subPaths];
};

/**
 * This methed will finds and set the meta data for subzone like what is the far left, right and bottom child
 * So that we can draw container around all the subzone stations using their positions
 */
const setSubzoneMetaData = (stations: Station[]) => {
    for (const station of stations) {
        const { id, type } = station;

        if (type === StationType.SubZone) {
            const subStations = stations.filter(({ parentStation }) => id === parentStation?.getId());
            const leastXPositionStation = subStations.reduce((a, b) => (a.xPosition < b.xPosition ? a : b));
            const highestXPositionStation = subStations.reduce((a, b) => (a.xPosition > b.xPosition ? a : b));
            const highestYPositionStation = subStations.reduce((a, b) => (a.yPosition > b.yPosition ? a : b));
            station.setLeftMostChild(leastXPositionStation);
            station.setRightMostChild(highestXPositionStation);
            station.setBottomMostChild(highestYPositionStation);
        }
    }
    return stations;
};

const updateStationsAbsolutePosition = (stations: Station[]) => {
    const lowestXValueInMap = getLowestXValueInMap(stations);
    for (const station of stations) {
        station.setAbsolutePosition(lowestXValueInMap);
    }
};

export const updateStationsData = (stations: Station[]) => {
    setSubzoneMetaData(stations);
    updateStationsAbsolutePosition(stations);
    return stations;
};

/**
 * moves loopback paths to the end of list
 */
const reOrderPaths = (paths: Path[]) => {
    return paths
        .reduce(
            (acc, path: Path) => {
                if (path.isLoopBackPath()) {
                    acc[1].push(path);
                } else {
                    acc[0].push(path);
                }
                return acc;
            },
            [[], []] as Path[][]
        )
        .reduce((acc, paths) => acc.concat(paths), []);
};

export const updatePathsData = (
    paths: Path[],
    stations: Station[],
    pathNodeCollection: PathNodeCollection,
    expandedStation: ExpandedStation,
    isMapBuilder: boolean
) => {
    const orderedPaths = reOrderPaths(paths);
    for (const path of orderedPaths) {
        path.setDrawablePath(stations, pathNodeCollection, expandedStation, isMapBuilder);
    }
    return orderedPaths;
};

/**
 * This method builds the section data to render section labels and background
 * Finds the min value to start rendering section till next station
 */
export const getSectionsData = (sections: APISectionData[], stations: Station[], expandedStation?: ExpandedStation) => {
    const findMinAbsoluteYValueInStations = (stations: Station[]) => {
        let min = stations.length ? stations[0].getAbsoluteYValue() : 0;
        for (const station of stations) {
            if (!station.isHidden(expandedStation)) {
                min = Math.min(station.getAbsoluteYValue(), min);
            }
        }
        return min;
    };

    const sectionsData: SectionData[] = sections.map((section, index) => {
        const sectionStations = stations.filter(({ sectionID }) => sectionID === section.id);
        const isFirstSection = index === 0;
        const min = isFirstSection ? 0 : findMinAbsoluteYValueInStations(sectionStations);

        return {
            ...section,
            min
        };
    });

    return sectionsData;
};
